import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import { getOneRosterInfo } from '../../store/OneRoster/OneRosterDashboardAction';
import StatusCode from '../../util/StatusCode';
import Loading from '../misc/Loading';
import { Link } from 'react-router-dom'
import * as Helper from '../../util/Helper';
import { CoreOneRosterDashboard } from '../../models/CoreOneRosterModels';
import { useTranslation } from 'react-i18next';
import { RequestState } from '../../models/RequestState';


interface DashboardState {
    oneroster: RequestState<CoreOneRosterDashboard>
}

interface State {
    onerosterDashboard: DashboardState
}
type CountSelector = (data: CoreOneRosterDashboard) => number;

interface Info {
    key: string
    name: string
    link: string
    selector: CountSelector
};


const OneRosterDashboard = () => {
    const { t } = useTranslation(['common', 'oneRoster']);
    const dispatch = useDispatch();
    const oneRoster = useSelector<State, RequestState<CoreOneRosterDashboard>>(state => state.onerosterDashboard.oneroster);

    useEffect(() => {
        getOneRosterInfo()(dispatch);
    }, [dispatch]);

    const info: Info[] = [
        { selector: (data) => data.schoolUnits, key: 'schoolUnits', name: t('object.oneRoster.school', { ns: 'common', count: 2 }), link: '/oneroster/schoolunit/' },
        { selector: (data) => data.classes, key: 'classes', name: t('object.oneRoster.section', { ns: 'common', count: 2 }), link: '/oneroster/class/' },
        { selector: (data) => data.teachers, key: 'teachers', name: t('commonTableHeaders.teachers', { ns: 'oneRoster' }), link: '/oneroster/user?role=teacher' },
        { selector: (data) => data.students, key: 'students', name: t('commonTableHeaders.students', { ns: 'oneRoster' }), link: '/oneroster/user?role=student' }
    ]

    const getCount = (selector: CountSelector): number => {
        if ((oneRoster.code === StatusCode.COMPLETE) && oneRoster.data) {
            return selector(oneRoster.data)
        }
        return 0;
    }

    const renderBody = () => {
        if (oneRoster.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (oneRoster.code === StatusCode.ERROR) {
            return <p>{t('error.somethingWentWrong', { ns: 'common' })}</p>
        }
        if (oneRoster.code === StatusCode.COMPLETE) {
            return (
                <div>
                    <h3>{t('dashboard.countActive', { ns: 'oneRoster' })}</h3>
                    <div className="list-group list-group-horizontal-lg">
                        {info.map(entry => {
                            return (
                                <Link to={entry.link} key={entry.key} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-fill">
                                    {entry.name}
                                    <span className="badge bg-primary rounded-pill">{getCount(entry.selector)}</span>
                                </Link>
                            )
                        })}
                    </div>

                    <h3 className="mt-3">{t('dashboard.sectionOverview', { ns: 'oneRoster' })}</h3>
                    <div className="mobile-header-table">Läsår</div>
                    <Table className="overwiew-table">
                        <thead>
                            <tr>
                                <th>{t('genericTableHeaders.startDate', { ns: 'common' })}</th>
                                <th>{t('genericTableHeaders.endDate', { ns: 'common' })}</th>
                                <th>{t('sectionOverview.activeNotProvisioned', { ns: 'oneRoster' })}</th>
                                <th>{t('sectionOverview.activeProvisioned', { ns: 'oneRoster' })}</th>
                                <th>{t('sectionOverview.inactive', { ns: 'oneRoster' })}</th>
                                <th>{t('sectionOverview.archived', { ns: 'oneRoster' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {oneRoster.data.classStats.map(x => (
                                <tr key={x.start}>
                                    <td data-label={t('genericTableHeaders.startDate', { ns: 'common' })}>{Helper.formatDateOnly(x.start)}</td>
                                    <td data-label={t('genericTableHeaders.endDate', { ns: 'common' })}>{Helper.formatDateOnly(x.end)}</td>
                                    <td data-label={t('sectionOverview.activeNotProvisioned', { ns: 'oneRoster' })}>{x.active}</td>
                                    <td data-label={t('sectionOverview.activeProvisioned', { ns: 'oneRoster' })}>{x.activeProvisioned}</td>
                                    <td data-label={t('sectionOverview.inactive', { ns: 'oneRoster' })}>{x.inactive}</td>
                                    <td data-label={t('sectionOverview.archived', { ns: 'oneRoster' })}>{x.archived}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )
        }
    }


    return (
        <div className="container dashboard_oneroster padding-40">
            <h1>{t('oneRoster', { ns: 'oneRoster' })}</h1>
            {renderBody()}
        </div>
    )
}

export default OneRosterDashboard;
